import { useEffect, useState } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

// ─── Card image imports ──────────────────────────────────────────────
import cardImg1 from "../../assets/b1.jpg";
import cardImg2 from "../../assets/events.jpeg";
import cardImg3 from "../../assets/workshop.jpg";
import cardImg4 from "../../assets/b4.jpg";
import cardImg5 from "../../assets/courses.jpeg";

// ─── Global & Tokens ────────────────────────────────────────────────
const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;1,300;1,400&family=Jost:wght@200;300;400&display=swap');
  *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }
  :root {
    --cream:       #F7F3EE;
    --cream-dark:  #EDE7DC;
    --warm-tan:    #C9AA8C;
    --blush:       #D9B8A2;
    --terracotta:  #B87355;
    --mocha:       #7A5C47;
    --ink:         #2E1F14;
    --ink-soft:    #5C4033;
    --gold:        #C49A6C;
    --font-serif:  'Cormorant Garamond', Georgia, serif;
    --font-sans:   'Montserrat', sans-serif;
  }
  html { scroll-behavior: smooth; }
  body {
    background: var(--cream);
    color: var(--ink);
    font-family: var(--font-sans);
    font-weight: 300;
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }
`;

// ─── Dust Particles ──────────────────────────────────────────────────
const floatAnim = keyframes`
  0%   { transform: translateY(0px) translateX(0px) rotate(0deg); opacity: 0; }
  10%  { opacity: 1; }
  90%  { opacity: 0.6; }
  100% { transform: translateY(-130px) translateX(25px) rotate(180deg); opacity: 0; }
`;

const Particle = styled.span`
  position: absolute;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  border-radius: 50%;
  background: var(--gold);
  opacity: 0;
  animation: ${floatAnim} ${({ $dur }) => $dur}s ease-in-out
    ${({ $delay }) => $delay}s infinite;
  left: ${({ $left }) => $left}%;
  top: ${({ $top }) => $top}%;
  pointer-events: none;

  @media (max-width: 600px) {
    display: ${({ $id }) => ($id % 2 === 0 ? "none" : "block")};
  }
`;

const particles = Array.from({ length: 22 }, (_, i) => ({
  id: i,
  size: Math.random() * 3 + 1,
  dur: Math.random() * 6 + 5,
  delay: Math.random() * 8,
  left: Math.random() * 100,
  top: Math.random() * 100,
}));

// ─── Layout ──────────────────────────────────────────────────────────
const HeroWrapper = styled.section`
  position: relative;
  width: 100vw;
  min-height: 100svh;
  background: var(--cream);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.035'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 1;
    opacity: 0.6;
  }
`;

const ParticleLayer = styled.div`
  position: absolute;
  inset: 0;
  z-index: 2;
  pointer-events: none;
`;

const DiagonalBand = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 60%;
  height: 100%;
  background: linear-gradient(160deg, var(--cream-dark) 0%, #e8d8c8 100%);
  clip-path: polygon(10% 0, 90% 0, 100% 100%, 0% 100%);
  z-index: 0;

  @media (max-width: 600px) {
    width: 120%;
    clip-path: polygon(17% 0, 92% 0, 100% 100%, 0% 100%);
    opacity: 0.6;
  }

  @media (min-width: 601px) and (max-width: 900px) {
    width: 90%;
    clip-path: polygon(9% 0, 91% 0, 100% 100%, 0% 100%);
  }
`;

const RuleBase = styled(motion.div)`
  position: absolute;
  background: var(--gold);
  opacity: 0.4;
`;
const RuleH = styled(RuleBase)`
  height: 1px;
  width: ${({ $w }) => $w || "80px"};
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};

  @media (max-width: 600px) {
    display: none;
  }
`;
const RuleV = styled(RuleBase)`
  width: 1px;
  height: ${({ $h }) => $h || "50px"};
  top: ${({ $top }) => $top};
  left: ${({ $left }) => $left};

  @media (max-width: 600px) {
    display: none;
  }
`;

// ─── Content grid ─────────────────────────────────────────────────────
const ContentGrid = styled.div`
  position: relative;
  z-index: 5;
  flex: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  align-items: center;
  justify-items: center;
  padding: 4rem 7vw 5rem;
  gap: 4rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 5rem 6vw 5rem;
    gap: 3rem;
  }

  @media (max-width: 600px) {
    padding: 3.5rem 6vw 4rem;
    gap: 2.5rem;
  }
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 600px;

  @media (max-width: 900px) {
    align-items: center;
    text-align: center;
    max-width: 540px;
    margin: 0 auto;
  }
`;

const EyebrowRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.8rem;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;
const Eyebrow = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.65rem;
  font-weight: 500;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--terracotta);

  @media (max-width: 400px) {
    font-size: 0.58rem;
    letter-spacing: 0.2em;
  }
`;
const EyebrowDash = styled(motion.span)`
  display: block;
  width: 40px;
  height: 1px;
  background: var(--terracotta);
  opacity: 0.7;
  flex-shrink: 0;
`;

const HeadlineWrap = styled.div`
  overflow: visible;
  margin-bottom: 0.2rem;
`;
const Headline = styled(motion.h1)`
  font-family: "Cormorant Garamond", serif;
  font-weight: 300;
  font-size: clamp(3rem, 8vw, 6.6rem);
  letter-spacing: -0.01em;
  color: #5a452a;

  @media (max-width: 600px) {
    font-size: clamp(2.8rem, 13vw, 4.5rem);
  }
`;
const HeadlineItalic = styled(Headline)`
  font-style: italic;
  color: #a18b6d;
`;
const SubLine = styled(motion.p)`
  font-family: var(--font-sans);
  font-size: clamp(0.78rem, 1vw, 0.88rem);
  letter-spacing: 0.07em;
  line-height: 1.9;
  color: #3d2e1a;
  max-width: 500px;
  margin-top: 2.2rem;

  @media (max-width: 900px) {
    max-width: 48ch;
    font-size: 0.85rem;
  }

  @media (max-width: 600px) {
    font-size: 0.82rem;
    line-height: 1.8;
    margin-top: 1.6rem;
  }
`;
const CTARow = styled.div`
  display: flex;
  align-items: center;
  gap: 2.5rem;
  margin-top: 2.8rem;

  @media (max-width: 900px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1.4rem;
    margin-top: 2rem;
    width: 100%;
  }
`;
const PrimaryBtn = styled(motion.button)`
  font-family: var(--font-sans);
  font-size: 0.72rem;
  font-weight: 400;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--cream);
  background: var(--ink);
  border: none;
  padding: 1.1rem 2.8rem;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background: var(--terracotta);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.4s ease;
    z-index: 0;
  }
  &:hover::after {
    transform: scaleX(1);
  }
  span {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 480px) {
    width: 100%;
    text-align: center;
    padding: 1.1rem 2rem;
  }
`;
const SecondaryLink = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.72rem;
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--mocha);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;

  svg {
    transition: transform 0.3s;
  }
  &:hover svg {
    transform: translateX(5px);
  }

  @media (max-width: 480px) {
    font-size: 0.7rem;
  }
`;

// ─── Card Deck ───────────────────────────────────────────────────────
const DeckOuter = styled(motion.div)`
  position: relative;
  width: clamp(260px, 32vw, 500px);
  height: clamp(340px, 42vw, 630px);
  margin-bottom: 44px;

  @media (max-width: 600px) {
    width: clamp(220px, 72vw, 300px);
    height: clamp(280px, 90vw, 380px);
    margin-bottom: 32px;
  }
`;

const GameCard = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background-color: var(--cream-dark);
  background-image: url(${({ $img }) => $img});
  background-size: cover;
  background-position: center top;
  box-shadow:
    0 4px 8px rgba(46, 31, 20, 0.08),
    0 16px 40px rgba(46, 31, 20, 0.16),
    0 40px 80px rgba(46, 31, 20, 0.1);

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    background: linear-gradient(
      130deg,
      rgba(255, 255, 255, 0.12) 0%,
      rgba(255, 255, 255, 0.03) 40%,
      transparent 65%
    );
    z-index: 2;
    pointer-events: none;
  }
  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 65%;
    background: linear-gradient(
      to top,
      rgba(28, 14, 6, 0.65) 0%,
      transparent 100%
    );
    z-index: 2;
    pointer-events: none;
  }
`;

const CardFace = styled.div`
  position: absolute;
  inset: 0;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 1.2rem 1.2rem 1.4rem;
`;

const CardTop = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const CardOrb = styled.div`
  width: 42px;
  height: 42px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  &::before {
    content: "";
    width: 26px;
    height: 26px;
    border: 1px solid rgba(255, 255, 255, 0.25);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const CardBottom = styled.div``;
const CardLabel = styled.div`
  font-family: var(--font-serif);
  font-size: 1.55rem;
  font-weight: 300;
  font-style: italic;
  color: rgba(255, 255, 255, 0.96);
  line-height: 1;
  letter-spacing: 0.01em;
`;
const CardSub = styled.div`
  font-family: var(--font-sans);
  font-size: 0.57rem;
  font-weight: 300;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.55);
  margin-top: 0.3rem;
`;

const DotRow = styled(motion.div)`
  position: absolute;
  bottom: -38px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
  align-items: center;

  @media (max-width: 600px) {
    bottom: -28px;
  }
`;
const Dot = styled.div`
  height: 6px;
  width: ${({ $active }) => ($active ? "20px" : "6px")};
  border-radius: 3px;
  background: ${({ $active }) =>
    $active ? "var(--terracotta)" : "var(--warm-tan)"};
  opacity: ${({ $active }) => ($active ? 1 : 0.35)};
  transition: all 0.45s cubic-bezier(0.22, 1, 0.36, 1);
  cursor: pointer;
  padding: 6px 0;
  box-sizing: content-box;
`;

// ─── Bottom strip ─────────────────────────────────────────────────────
const BottomStrip = styled.div`
  position: relative;
  z-index: 5;
  display: none;
  align-items: center;
  justify-content: space-between;
  padding: 1.2rem 5vw;
  border-top: 1px solid rgba(122, 92, 71, 0.12);

  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0.8rem;
    padding: 1.2rem 6vw;
    text-align: center;
  }
`;
const StripText = styled(motion.span)`
  font-family: var(--font-sans);
  font-size: 0.62rem;
  font-weight: 300;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--warm-tan);

  @media (max-width: 400px) {
    font-size: 0.55rem;
    letter-spacing: 0.15em;
  }
`;
const SocialRow = styled.div`
  display: flex;
  gap: 1.6rem;

  @media (max-width: 600px) {
    gap: 1.2rem;
  }
`;

// ─── Variants ────────────────────────────────────────────────────────
const revealUp = {
  hidden: { y: "102%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = (delay = 0) => ({
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: delay } },
});

function slotStyle(slot, total) {
  if (slot === 0) return { rotate: 0, x: 0, y: 0, scale: 1, zIndex: total };
  return {
    rotate: slot * 5.5,
    x: slot * 12,
    y: slot * -6,
    scale: 1 - slot * 0.035,
    zIndex: total - slot,
  };
}

// ─── CardDeck ────────────────────────────────────────────────────────
function CardDeck({ mounted, CARDS }) {
  const [active, setActive] = useState(0);
  const total = CARDS.length;

  useEffect(() => {
    if (!mounted) return;
    const t = setInterval(() => setActive((p) => (p + 1) % total), 5000);
    return () => clearInterval(t);
  }, [mounted, total]);

  const goNext = () => setActive((p) => (p + 1) % total);
  const goPrev = () => setActive((p) => (p - 1 + total) % total);

  const order = Array.from({ length: total }, (_, i) => (i + active) % total);

  return (
    <DeckOuter
      initial={{ opacity: 0, y: 36, rotate: -4 }}
      animate={{ opacity: 1, y: 0, rotate: 0 }}
      transition={{ delay: 1.0, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
    >
      {order.map((cardIdx, slot) => {
        const card = CARDS[cardIdx];
        const s = slotStyle(slot, total);
        return (
          <GameCard
            key={card.id}
            $img={card.img}
            animate={{
              rotate: s.rotate,
              x: s.x,
              y: s.y,
              scale: s.scale,
              zIndex: s.zIndex,
            }}
            transition={{
              type: "spring",
              stiffness: 280,
              damping: 28,
              mass: 0.85,
            }}
            onClick={() => (slot === 0 ? goNext() : setActive(cardIdx))}
            whileHover={slot === 0 ? { y: -10, rotate: -1.5, scale: 1.03 } : {}}
            drag={slot === 0 ? "x" : false}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.25}
            onDragEnd={(_, info) => {
              if (info.offset.x < -60) goNext();
              else if (info.offset.x > 60) goPrev();
            }}
          >
            <CardFace>
              <CardTop>
                <CardOrb />
              </CardTop>
              <CardBottom>
                <CardLabel>{card.label}</CardLabel>
                <CardSub>{card.sub}</CardSub>
              </CardBottom>
            </CardFace>
          </GameCard>
        );
      })}

      <DotRow
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.9 }}
      >
        {CARDS.map((c, i) => (
          <Dot key={c.id} $active={i === active} onClick={() => setActive(i)} />
        ))}
      </DotRow>
    </DeckOuter>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────
export default function HeroSection() {
  const { t } = useTranslation();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setReady(true), 80);
    return () => clearTimeout(t);
  }, []);

  // ─── Cards with translated strings ─────────────────────────────────
  const CARDS = [
    {
      id: 0,
      label: t("hero.cards.bridal.label"),
      sub: t("hero.cards.bridal.sub"),
      img: cardImg1,
    },
    {
      id: 1,
      label: t("hero.cards.events.label"),
      sub: t("hero.cards.events.sub"),
      img: cardImg2,
    },
    {
      id: 2,
      label: t("hero.cards.shooting.label"),
      sub: t("hero.cards.shooting.sub"),
      img: cardImg4,
    },
    {
      id: 3,
      label: t("hero.cards.workshop.label"),
      sub: t("hero.cards.workshop.sub"),
      img: cardImg3,
    },
    {
      id: 4,
      label: t("hero.cards.courses.label"),
      sub: t("hero.cards.courses.sub"),
      img: cardImg5,
    },
  ];

  return (
    <>
      <GlobalStyle />
      <AnimatePresence>
        {ready && (
          <HeroWrapper>
            <ParticleLayer>
              {particles.map((p) => (
                <Particle
                  key={p.id}
                  $id={p.id}
                  $size={p.size}
                  $dur={p.dur}
                  $delay={p.delay}
                  $left={p.left}
                  $top={p.top}
                />
              ))}
            </ParticleLayer>

            <DiagonalBand
              initial={{ opacity: 0, x: 80 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            />

            <RuleH
              $top="22%"
              $left="5vw"
              $w="80px"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 1.4, duration: 0.8 }}
              style={{ transformOrigin: "left" }}
            />
            <RuleV
              $top="15%"
              $left="calc(5vw + 80px)"
              $h="50px"
              initial={{ scaleY: 0 }}
              animate={{ scaleY: 1 }}
              transition={{ delay: 1.6, duration: 0.6 }}
              style={{ transformOrigin: "top" }}
            />

            <ContentGrid>
              <LeftCol>
                <EyebrowRow>
                  <EyebrowDash
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.5, duration: 0.6 }}
                    style={{ transformOrigin: "left" }}
                  />
                  <Eyebrow
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.7, duration: 0.6 }}
                  >
                    {t("hero.eyebrow")}
                  </Eyebrow>
                </EyebrowRow>

                <motion.div
                  variants={stagger(0.85)}
                  initial="hidden"
                  animate="show"
                >
                  <HeadlineWrap>
                    <Headline variants={revealUp}>
                      {t("hero.headline_1")}
                    </Headline>
                  </HeadlineWrap>
                  <HeadlineWrap>
                    <HeadlineItalic as={motion.h1} variants={revealUp}>
                      {t("hero.headline_2")}
                    </HeadlineItalic>
                  </HeadlineWrap>
                  <HeadlineWrap>
                    <Headline variants={revealUp}>
                      {t("hero.headline_3")}
                    </Headline>
                  </HeadlineWrap>
                </motion.div>

                <SubLine
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1.35, duration: 0.9 }}
                >
                  {t("hero.subline")}
                </SubLine>

                <CTARow>
                  <Link to={"/portfolio"}>
                    <PrimaryBtn
                      initial={{ opacity: 0, y: 16 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 1.6, duration: 0.7 }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <span>{t("hero.cta_portfolio")}</span>
                    </PrimaryBtn>
                  </Link>
                  <Link to={"/services"} className="link-styles">
                    <SecondaryLink
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.85, duration: 0.7 }}
                    >
                      {t("hero.cta_services")}
                      <svg
                        width="18"
                        height="10"
                        viewBox="0 0 18 10"
                        fill="none"
                      >
                        <path
                          d="M1 5h16M12 1l5 4-5 4"
                          stroke="currentColor"
                          strokeWidth="1"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </SecondaryLink>
                  </Link>
                </CTARow>
              </LeftCol>

              <CardDeck mounted={ready} CARDS={CARDS} />
            </ContentGrid>

            <BottomStrip>
              <StripText
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2.1, duration: 0.8 }}
              >
                {t("hero.footer_available")}
              </StripText>
              <SocialRow>
                {["Instagram", "Pinterest", "TikTok"].map((s, i) => (
                  <StripText
                    key={s}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 2.2 + i * 0.1, duration: 0.6 }}
                    style={{ cursor: "pointer" }}
                  >
                    {s}
                  </StripText>
                ))}
              </SocialRow>
            </BottomStrip>
          </HeroWrapper>
        )}
      </AnimatePresence>
    </>
  );
}
